import { useState } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, Navigate, Route, RouterProvider, Routes } from 'react-router';
import { queryClient } from './api/queryClient';
import { AuthArrivalRedirect } from './auth/AuthArrivalRedirect';
import { RequireSession } from './auth/RequireSession';
import { SessionProvider } from './auth/SessionProvider';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AppShell } from './layout/AppShell';
import { DocumentMeta } from './seo/DocumentMeta';
import { AccessCallbackScreen } from './screens/AccessCallbackScreen';
import { AnalysisInterestScreen } from './screens/AnalysisInterestScreen';
import { AssistedPromptScreen } from './screens/AssistedPromptScreen';
import { GeneratePromptScreen } from './screens/GeneratePromptScreen';
import { GeneratorIntroScreen } from './screens/GeneratorIntroScreen';
import { FeedbackScreen } from './screens/FeedbackScreen';
import { RevenueCatProvider } from './revenuecat/RevenueCatProvider';
import { RequireProductAccess } from './access/RequireProductAccess';
import { LibraryScreen } from './screens/LibraryScreen';
import { NotFoundScreen } from './screens/NotFoundScreen';
import { PinScreen } from './screens/PinScreen';
import { PinsScreen } from './screens/PinsScreen';
import { InDevelopmentScreen } from './screens/InDevelopmentScreen';
import { MediaKitScreen } from './screens/MediaKitScreen';
import { ProfileOnboardingScreen } from './screens/ProfileOnboardingScreen';
import { ScriptsScreen } from './screens/ScriptsScreen';
import { PasswordResetScreen } from './screens/PasswordResetScreen';
import { PasswordSetupScreen } from './screens/PasswordSetupScreen';
import { PasswordSignInScreen } from './screens/PasswordSignInScreen';
import { PromptScreen } from './screens/PromptScreen';
import { SalesScreen } from './screens/SalesScreen';
import { SignInScreen } from './screens/SignInScreen';

/**
 * The route table is the flow. Public routes are the three a person can reach
 * before she has a session — sale, sign-in, resend — plus the one the magic
 * link lands on. Everything else sits behind `RequireSession`.
 */
export function AppRoutes(): JSX.Element {
  return (
    <Routes>
      <Route path="/" element={<SalesScreen />} />
      <Route path="/entrar" element={<SignInScreen />} />
      <Route path="/reenviar" element={<SignInScreen variant="resend" />} />
      {/* The second door (ADR-0019). Public for the same reason the three above
          are: someone who cannot sign in yet is exactly who needs them. */}
      <Route path="/entrar/senha" element={<PasswordSignInScreen />} />
      <Route path="/senha/recuperar" element={<PasswordResetScreen />} />
      {/* Two generator screens changed URL. Both old paths stay alive
          permanently: a lifetime product cannot afford a dead bookmark, and the
          access e-mail already sent points at them.

          Public, above the session guard, because a rename is a rewrite and has
          nothing to do with who is asking. Behind the guard, an old link from a
          signed-out mailbox would be remembered by its dead name and land her
          somewhere that no longer exists after she signs in. */}
      <Route path="/gerador/gerar" element={<Navigate to="/gerador/montar" replace />} />
      <Route path="/gerador/salvos" element={<Navigate to="/gerador/fixados" replace />} />
      <Route element={<RequireSession />}>
        <Route path="/acesso" element={<AccessCallbackScreen />} />
        {/* Behind the guard, and outside the shell: a password is only ever set
            from inside a session, and this is also where a recovery link lands. */}
        <Route path="/senha/definir" element={<PasswordSetupScreen />} />
        <Route element={<AppShell />}>
          <Route path="/biblioteca" element={<LibraryScreen />} />
          <Route path="/biblioteca/:slug" element={<PromptScreen />} />
          <Route path="/gerador" element={<GeneratorIntroScreen />} />
          <Route path="/gerador/perfil" element={<ProfileOnboardingScreen />} />
          <Route path="/midia-kit" element={<MediaKitScreen />} />
          {/* Announced and unbuilt. One screen, three surfaces: the difference
              between them is a question, and a route each is what lets the
              padlock in the menu land somewhere that explains itself. */}
          <Route path="/ideias" element={<InDevelopmentScreen surface="ideias" />} />
          <Route path="/memorias" element={<InDevelopmentScreen surface="memorias" />} />
          <Route path="/agentes" element={<InDevelopmentScreen surface="agentes" />} />
          {/* The deterministic assembler. It was always this; the URL now says
              so, which is what frees the name below for the assisted one. */}
          <Route
            path="/gerador/montar"
            element={
              <RequireProductAccess product="prompt-generator">
                <GeneratePromptScreen />
              </RequireProductAccess>
            }
          />
          <Route path="/gerador/ia" element={<AssistedPromptScreen />} />
          <Route path="/gerador/fixados" element={<PinsScreen />} />
          <Route path="/gerador/fixados/:id" element={<PinScreen />} />
          <Route path="/roteiros" element={<ScriptsScreen />} />
          <Route path="/analise" element={<AnalysisInterestScreen />} />
          <Route path="/opiniao" element={<FeedbackScreen />} />
        </Route>
      </Route>
      {/* Last, and public. An address that matches nothing used to render
          nothing — a blank page answered with 200, which reads as an outage to
          a person and as a soft 404 to a crawler. It sits outside the session
          guard because a typo is not a permission question: sending her to
          sign in would answer the wrong one. */}
      <Route path="*" element={<NotFoundScreen />} />
    </Routes>
  );
}

function RoutedApp(): JSX.Element {
  return (
    <>
      {/* Before the routes, because it decides which of them she should
          be looking at when she arrives from her inbox. */}
      <AuthArrivalRedirect />
      {/* Beside the routes, not inside one: every screen needs a head
          and no screen should have to remember to write it. */}
      <DocumentMeta />
      <AppRoutes />
    </>
  );
}

function createAppRouter(): ReturnType<typeof createBrowserRouter> {
  return createBrowserRouter([{ path: '*', element: <RoutedApp /> }]);
}

export function App(): JSX.Element {
  const [router] = useState(createAppRouter);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <SessionProvider>
          <RevenueCatProvider>
            <RouterProvider router={router} />
          </RevenueCatProvider>
        </SessionProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
